import { useState } from 'react';

import { notify } from '../../app';
import {
  getLivekitServers,
  createLivekitServer,
  updateLivekitServer,
  deleteLivekitServer,
} from '../../api';
import type {
  LivekitServerCreateRequest,
  LivekitServerUpdateRequest,
} from '../../api';
import { useFetch, useModal, useRequest } from '../../hooks';
import type { LivekitServerDto } from '../../services/agents-management';

export const useLiveKitServers = () => {
  const { isOpened, onOpen, onClose } = useModal();

  const [currentForm, setCurrentForm] = useState<
    Partial<LivekitServerDto> | undefined
  >(undefined);
  const currentFormId = currentForm?.id;

  const {
    data: servers = [],
    intializing: loading,
    loading: reloading,
    refresh,
  } = useFetch<LivekitServerDto[]>(() => getLivekitServers(), []);

  const [handleAddServer, adding] = useRequest(
    (_signal, values: LivekitServerUpdateRequest) =>
      createLivekitServer(values as LivekitServerCreateRequest),
    {
      onSuccess: () => {
        refresh?.();
        onClose?.();
        notify.success('LiveKit server created');
      },
      onError: (error: any) => {
        notify.error(
          `Failed to create LiveKit server: ${error?.body?.detail ?? 'No details'}`
        );
      },
      deps: [],
    }
  );

  const [handleEditServer, updating] = useRequest(
    (_signal, values: LivekitServerUpdateRequest) => {
      if (!currentFormId) return null;
      console.log(values);
      return updateLivekitServer(currentFormId, values);
    },
    {
      onSuccess: () => {
        refresh?.();
        onClose?.();
        notify.success('LiveKit server updated');
      },
      onError: (error: any) => {
        notify.error(
          `Failed to update LiveKit server: ${error?.body?.detail ?? 'No details'}`
        );
      },
      deps: [currentFormId],
    }
  );

  const {
    isOpened: openedDeleteModal,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useModal();
  const [serverForDelete, setServerForDelete] = useState<
    LivekitServerDto | undefined
  >();
  const handleOpenDeleteServerModal = (server: LivekitServerDto) => {
    setServerForDelete(server);
    openDeleteModal();
  };
  const [handleDeleteServer, deleting] = useRequest(
    async _token => {
      if (!serverForDelete?.id) return false;

      return await deleteLivekitServer(serverForDelete.id);
    },
    {
      deps: [serverForDelete],
      onSuccess: () => {
        notify.success('LiveKit server deleted');
        refresh?.();
        closeDeleteModal();
      },
      onError: (error: any) =>
        notify.error(
          `Failed to delete LiveKit server: ${error?.body?.detail ?? 'No details'}`
        ),
    }
  );

  return {
    servers,
    loading,
    reloading,
    refresh,

    isOpened,
    onOpen,
    onClose,

    adding,
    updating,
    handleAddServer,
    handleEditServer,

    currentForm,
    isEdit: !!currentFormId,
    setCurrentForm,

    deleting,
    serverForDelete,
    openedDeleteModal,
    closeDeleteModal,
    handleDeleteServer,
    openDeleteModal: handleOpenDeleteServerModal,
  };
};

export type {
  LivekitServerDto,
  LivekitServerCreateRequest,
  LivekitServerUpdateRequest,
};
